import type { Post } from '@/@types/post'
import BubbleIcon from '@/assets/bubble.svg?react'
import MoreMenuButton from '@/components//MoreMenuButton'
import { BlurTextMode, FeaturableBlurTextSwitch } from '@/components/BlurTextSwitch'
import useUniversalNavigate from '@/utils/hooks/useUniversalNavigate'
import { POST_TYPE_TO_ROUTE_MAPPING } from '@/utils/route'
import clsx from 'clsx'
import { isEqual } from 'lodash-es'
import { type HTMLAttributes, memo } from 'react'
import ActionButton from '../../ActionButton'
import DynamicLikeIcon from '../DynamicLikeIcon'
import { usePostActionsContext } from '../PostActionsContext'
import styles from './index.module.css'
import RepostMenuButton from './RepostMenuButton'
import useMoreActionsMenuData from './useMoreActionsMenuData'
import useMoreActionsMenuSelectHandler from './useMoreActionsMenuSelectHandler'
import { usePostLikeToggle } from './usePostActionHandlers'

export interface PostActionsProps extends HTMLAttributes<HTMLDivElement> {
	data: Post
}

export default memo(function PostActions({ data, className, ...props }: PostActionsProps) {
	const { id, user, likeCount = 0, commentCount = 0 } = data
	const { toggleLike } = usePostLikeToggle(data.id)
	const navigate = useUniversalNavigate()

	const { onComment } = usePostActionsContext()

	const moreMenuData = useMoreActionsMenuData(data)
	const handleMoreActionMenuItemClick = useMoreActionsMenuSelectHandler(data)

	return (
		<div {...props} key={`post-actions-${id}`} className={clsx(styles.root, className)}>
			<ActionButton
				tooltip="点赞"
				onClick={() => toggleLike()}
				className={styles.likeButton}
				icon={
					<DynamicLikeIcon
						data={data}
						className={clsx(styles.likeIcon, data?.liked && styles.likeIconLiked)}
					/>
				}
			>
				<FeaturableBlurTextSwitch data={likeCount.toString()} mode={BlurTextMode.SMALL} />
			</ActionButton>

			<ActionButton
				tooltip="评论"
				onClick={(e) => {
					if (onComment) {
						onComment(data)
						return
					}
					navigate(
						{
							to: '/u/$username/$type/$id',
							params: {
								username: user?.username,
								id,
								type: POST_TYPE_TO_ROUTE_MAPPING[data.type],
							},
							hash: 'comments',
						},
						e
					)
				}}
				icon={<BubbleIcon width="20px" height="20px" className={styles.commentIcon} />}
			>
				<FeaturableBlurTextSwitch data={commentCount.toString()} mode={BlurTextMode.SMALL} />
			</ActionButton>

			<RepostMenuButton data={data} />

			<section className={styles.moreSection}>
				<MoreMenuButton data={moreMenuData} onSelect={handleMoreActionMenuItemClick} />
			</section>
		</div>
	)
}, isEqual)
